/**
 * clients.ts
 * Shared Zod contracts for the identity clients API.
 *
 * Clients are kernel/identity-owned — company_id always comes from TenantContext,
 * never from the request body (KC-3.5). These schemas are consumed by both the
 * kernel routes (backend) and the frontend API layer.
 *
 * Spec: KI-5 / design §5a
 */
import { z } from 'zod';
import type { PaginatedResponse } from './pagination.js';

// ---------------------------------------------------------------------------
// Client read schema (wire shape returned by GET /clients and POST /clients)
// ---------------------------------------------------------------------------

export const ClientSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  active: z.boolean(),
  clientTypeId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  // add-kernel-client-enrichment: profile fields, all optional at the wire
  // boundary — a client created before this change has them as null.
  cuit: z.string().nullable(),
  phone: z.string().nullable(),
  address: z.string().nullable(),
  email: z.string().nullable(),
  photoFileId: z.string().uuid().nullable(),
  // add-kernel-organization-identity: read-only on this endpoint. When set,
  // name/cuit/phone/address/email above are resolved from identity.organization
  // instead of this client's own columns (design.md §4) — the wire SHAPE never
  // changes, only where the values come from. Linking happens through the
  // client-onboarding flow (Slice 4), never a direct PATCH of this field.
  organizationId: z.string().uuid().nullable(),
  // cross-tenant-kernel-mechanism PR7: read-only, resolved from
  // organization.linkedCompanyId when organizationId is set (null otherwise).
  // The SOLE signal the admin UI uses to decide whether "this client is also
  // a Suite tenant" — i.e. whether the partnership invite action can render
  // at all (design.md CT1 / kernel-identity "linkedCompanyId as the Tenant-
  // Partnership Anchor"). Setting it alone grants nothing — a separate active
  // tenant_partnership row is still required to read/act on anything.
  linkedCompanyId: z.string().uuid().nullable(),
});

export type Client = z.infer<typeof ClientSchema>;

// ---------------------------------------------------------------------------
// Paginated list response
// ---------------------------------------------------------------------------

/** PaginatedResponse<Client> — reuses the kernel PaginatedResponse shape. */
export type ClientListResponse = PaginatedResponse<Client>;

// ---------------------------------------------------------------------------
// Create client body
// company_id NEVER in the body — sourced from TenantContext (KC-3.5).
// ---------------------------------------------------------------------------

export const CreateClientBodySchema = z.object({
  /** Required: client display name. */
  name: z.string().min(1, 'Client name is required.').max(256),
  /** Optional: FK to catalog.client_type. */
  clientTypeId: z.string().uuid().optional(),
  cuit: z.string().max(32).optional(),
  phone: z.string().max(64).optional(),
  address: z.string().max(512).optional(),
  email: z.string().email().max(256).optional(),
});

export type CreateClientBody = z.infer<typeof CreateClientBodySchema>;

// ---------------------------------------------------------------------------
// Update client body — PATCH /clients/:id (admin only). Same mutable-field
// shape as the shared catalog entries (CreateNamedCatalogEntrySchema's update
// counterpart in catalog-admin.routes.ts): rename and/or soft-deactivate.
// add-kernel-client-enrichment: also mutable — profile fields.
//
// photoFileId: two-step "upload then associate" flow, same pattern already
// proven by the daily-report photo gallery (POST /files → PATCH the parent
// entity with the returned fileId) — no dedicated upload route needed here.
// Nullable so a caller can clear the photo by sending null explicitly.
// ---------------------------------------------------------------------------

export const UpdateClientBodySchema = z.object({
  name: z.string().min(1, 'Client name is required.').max(256).optional(),
  active: z.boolean().optional(),
  clientTypeId: z.string().uuid().optional(),
  cuit: z.string().max(32).optional(),
  phone: z.string().max(64).optional(),
  address: z.string().max(512).optional(),
  email: z.string().email().max(256).optional(),
  photoFileId: z.string().uuid().nullable().optional(),
});

export type UpdateClientBody = z.infer<typeof UpdateClientBodySchema>;

// ---------------------------------------------------------------------------
// Client contract — identity.client_contract wire shape (add-kernel-client-
// enrichment, design.md §3). `status` is DERIVED (deriveContractStatus below),
// never persisted — computed fresh on every read so it can never drift out of
// sync with startDate/endDate.
// ---------------------------------------------------------------------------

export const ContractStatusSchema = z.enum(['vigente', 'proximo_a_vencer', 'vencido']);
export type ContractStatus = z.infer<typeof ContractStatusSchema>;

/**
 * Derives contract vigencia status from dates alone — never stored as a
 * column (design.md §3), so it can't drift out of sync with reality.
 *
 * vencido            : endDate < today
 * proximo_a_vencer   : today <= endDate <= today + 30 days
 * vigente            : otherwise
 *
 * @param startDate  'YYYY-MM-DD' (unused in the derivation, kept for callers
 *                    that want to validate the range hasn't started yet — v1
 *                    always returns a value assuming the contract is active).
 * @param endDate    'YYYY-MM-DD'
 * @param today      'YYYY-MM-DD' — injected (never `new Date()` internally)
 *                    so the function stays pure and deterministic in tests.
 */
export function deriveContractStatus(startDate: string, endDate: string, today: string): ContractStatus {
  const end = new Date(`${endDate}T00:00:00Z`).getTime();
  const now = new Date(`${today}T00:00:00Z`).getTime();
  const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

  if (end < now) return 'vencido';
  if (end <= now + THIRTY_DAYS_MS) return 'proximo_a_vencer';
  return 'vigente';
}

export const ClientContractSchema = z.object({
  id: z.string().uuid(),
  clientId: z.string().uuid(),
  contractTypeId: z.string().uuid().nullable(),
  number: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  createdAt: z.string().datetime(),
  status: ContractStatusSchema,
});

export type ClientContract = z.infer<typeof ClientContractSchema>;

/** ClientContract list response — reuses the kernel PaginatedResponse shape. */
export type ClientContractListResponse = PaginatedResponse<ClientContract>;

// company_id NEVER in the body — sourced from TenantContext (KC-3.5).
// clientId comes from the URL path (/identity/clients/:clientId/contracts).
export const CreateClientContractBodySchema = z
  .object({
    contractTypeId: z.string().uuid().optional(),
    number: z.string().min(1, 'Contract number is required.').max(64),
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'startDate must be YYYY-MM-DD.'),
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'endDate must be YYYY-MM-DD.'),
  })
  .refine((body) => body.endDate >= body.startDate, {
    message: 'endDate must not be before startDate.',
    path: ['endDate'],
  });

export type CreateClientContractBody = z.infer<typeof CreateClientContractBodySchema>;

export const UpdateClientContractBodySchema = z.object({
  contractTypeId: z.string().uuid().optional(),
  number: z.string().min(1, 'Contract number is required.').max(64).optional(),
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'startDate must be YYYY-MM-DD.').optional(),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'endDate must be YYYY-MM-DD.').optional(),
});

export type UpdateClientContractBody = z.infer<typeof UpdateClientContractBodySchema>;
